import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { DealViewRawBids, DealViewRawDeal, DealViewRawInvoices, DealViewRawOffers, DealViewRawStatus } from '@tradecafe/types/core'
import { DeepReadonly } from '@tradecafe/types/utils'
import { pickBy } from 'lodash-es'
import { BehaviorSubject, Observable } from 'rxjs'
import { map, take } from 'rxjs/operators'
import { AuthApiService } from 'src/api/auth'
import { updateAccountSuccess } from 'src/app/store/accounts'
import { patchDealProduct, patchDealProductFailure, patchDealProductSuccess } from 'src/app/store/deal-view.actions'
import { DealFormGroup, DealProductFormGroup } from 'src/pages/admin/trading/deal-form/deal-form-page/deal-form.schema'
import { DealFormService } from 'src/pages/admin/trading/deal-form/deal-form-page/deal-form.service'
import { AccountsService } from 'src/services/data/accounts.service'
import { ToasterService } from 'src/shared/toaster/toaster.service'


export type DealProductOverlayDealViewRaw = DeepReadonly<DealViewRawDeal & DealViewRawOffers & DealViewRawBids & DealViewRawInvoices & DealViewRawStatus>

export interface DealProductOverlayOptions {
  dealViewRaw$: Observable<DealProductOverlayDealViewRaw>
  dealForm: DealFormGroup
  productForm: DealProductFormGroup
}

@Component({
  selector: 'tc-deal-product-overlay',
  templateUrl: './deal-product-overlay.component.html',
  styleUrls: ['./deal-product-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DealProductOverlayComponent implements OnInit, OnDestroy {
  private actions$ = inject(Actions)
  private store = inject(Store)
  private dialogRef = inject<MatDialogRef<DealProductOverlayComponent>>(MatDialogRef)
  data = inject<DealProductOverlayOptions>(MAT_DIALOG_DATA)
  private Accounts = inject(AccountsService)
  private toaster = inject(ToasterService)
  private AuthApi = inject(AuthApiService)
  private DealForm = inject(DealFormService)


  inProgress$ = new BehaviorSubject(false)
  protected readonly viewOnly = this.AuthApi.currentUser.role === 'trader'
  dealId = this.data.dealForm.value.details.deal.deal_id
  productIndex = this.data.dealForm.controls.products.controls.indexOf(this.data.productForm)
  protected readonly dealViewRaw$ = this.data.dealViewRaw$
  protected readonly invoices$ = this.data.dealViewRaw$.pipe(map(dv => dv.invoices))
  private lockedForViewOnly = false

  ngOnInit() {
    if (this.viewOnly && this.data.productForm.enabled) {
      this.data.productForm.disable({ emitEvent: false })
      this.lockedForViewOnly = true
    }
  }

  ngOnDestroy() {
    if (!this.lockedForViewOnly) return

    let currentDealViewRaw: DealProductOverlayDealViewRaw | undefined
    const subscription = this.dealViewRaw$.pipe(take(1)).subscribe(dv => {
      currentDealViewRaw = dv
    })
    subscription.unsubscribe()

    this.data.productForm.enable({ emitEvent: false })
    if (currentDealViewRaw) {
      this.DealForm.enableDisableProductForm(
        this.data.productForm,
        currentDealViewRaw,
      )
    }
    this.data.productForm.updateValueAndValidity({ emitEvent: true })
  }

  cancel() {
    this.dialogRef.close()
  }

  private async updateBuyerProductsSpec(
    buyerId: string,
    products: Array<{ productId: string; itemTypeId: string; hsCode: string;}>
  ): Promise<void> {
    try {
      const buyer = await this.Accounts.getAccountById(buyerId)
      if (!buyer) {
        return
      }

      // Initialize products_spec if it doesn't exist
      const payload: any[] = buyer.products_spec ? [...buyer.products_spec] : []

      let hasChanges = false
      products.forEach(product => {
        // Skip if HS Code is empty
        if (!product.hsCode || product.hsCode.trim() === '') {
          return
        }

        const index = payload.findIndex(spec =>
          spec.product_id === product.productId &&
          spec.item_type_id === product.itemTypeId
        )

        if (index >= 0) {
          // Update existing entry
          if (payload[index].hs_code !== product.hsCode) {
            payload[index] = { ...payload[index], hs_code: product.hsCode }
            hasChanges = true
          }
        } else {
          // Create new entry if HS Code is not empty
          payload.push({
            product_id: product.productId,
            item_type_id: product.itemTypeId,
            hs_code: product.hsCode
          })
          hasChanges = true
        }
      })

      if (hasChanges) {
        const updatedAccount = await this.Accounts.updateAccountProducts(buyer, payload)
        this.store.dispatch(updateAccountSuccess({ account: updatedAccount }))
        this.toaster.success('HS Code preferences updated', 'Future deals will use the new HS Code')
      }
    } catch (error) {
      console.error('Failed to update buyer products_spec:', error)
      this.toaster.warning('HS Code preferences not updated', 'Deal saved successfully, but HS Code preferences could not be updated')
    }
  }

  submit() {
    if (this.viewOnly) return
    if (this.inProgress$.value) return
    this.data.productForm.markAllAsTouched()
    this.data.productForm.updateValueAndValidity()
    // NOTE: dirty flag isn't recursive. changing `batches[].lot` would not make `batches` dirty.
    if (!this.data.productForm.controls.batches.dirty && this.data.productForm.controls.batches.controls.find(c => c.dirty)) {
      this.data.productForm.controls.batches.markAsDirty()
    }

    if (!this.data.productForm.valid) return
    this.inProgress$.next(true)
    const deal = this.data.dealForm.value.details.deal
    const dealForm = this.data.dealForm.serialize()
    const patch = pickBy(this.data.productForm.serialize(), (_value, key) =>
      this.data.productForm.controls[key]?.dirty)
    this.dealViewRaw$.pipe(take(1)).subscribe(dv => {
      this.store.dispatch(patchDealProduct({ index: this.productIndex, dv, dealForm, patch }))
      this.actions$.pipe(ofType(patchDealProductSuccess, patchDealProductFailure), take(1)).subscribe(async (action) => {
        if (action.type === patchDealProductSuccess.type) {
          // Update buyer's products_spec if HS Code was changed
          if (patch.hsCode && this.data.productForm.controls.hsCode.dirty) {
            await this.updateBuyerProductsSpec(deal.buyer_id, [{
              productId: this.data.productForm.getRawValue().productId,
              itemTypeId: this.data.productForm.getRawValue().itemTypeId,
              hsCode: patch.hsCode as string,
            }])
          }
          this.dialogRef.close()
        }
        this.inProgress$.next(false)
      })
    })
  }
}
